"use client";

import React, { useState } from "react";

import { useTranslations } from "next-intl";

import { cn } from "@/utils/cn";

import styles from "../style.module.scss";
import { shouldShowSection } from "../utils/fieldUtils";

import ConditionalField from "./ConditionalField";

interface FieldConfig {
  /** Значение поля */
  value: any;
  /** Ключ для перевода лейбла */
  labelKey: string;
  /** Кастомный лейбл (если не нужен перевод) */
  customLabel?: string;
  /** Кастомная функция рендера значения */
  renderValue?: (value: any) => React.ReactNode;
  /** Показывать ли поле, даже если значение пустое */
  forceShow?: boolean;
  /** Placeholder для пустого значения */
  emptyPlaceholder?: string;
  /** Уникальный ключ поля */
  key?: string;
}

interface FieldSectionProps {
  /** Заголовок секции */
  title: string;
  /** Ключ для перевода заголовка */
  titleKey?: string;
  /** Конфигурация полей */
  fields: FieldConfig[];
  /** Можно ли сворачивать секцию */
  collapsible?: boolean;
  /** Начальное состояние сворачивания */
  defaultCollapsed?: boolean;
  /** CSS классы для контейнера */
  className?: string;
  /** Тестовый идентификатор */
  "data-testid"?: string;
  /** Показывать секцию только если есть заполненные поля */
  hideIfEmpty?: boolean;
}

export const FieldSection: React.FC<FieldSectionProps> = ({
  title,
  titleKey,
  fields,
  collapsible = false,
  defaultCollapsed = false,
  className,
  "data-testid": testId,
  hideIfEmpty = false,
}) => {
  const t = useTranslations();
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const sectionTitle = titleKey ? t(titleKey) : title;

  // Filter fields that have values or should be forced to show
  const visibleFields = fields.filter(
    (field) =>
      field.forceShow ||
      (field.value !== null && field.value !== undefined && field.value !== ""),
  );

  // If no visible fields and hideIfEmpty is true, don't render
  if (hideIfEmpty && visibleFields.length === 0) {
    return null;
  }

  return (
    <div
      className={className}
      data-testid={testId}
    >
      <h3 className="text-[#323232] font-medium text-[18px] mb-[20px]">
        {sectionTitle}
      </h3>
      {collapsible && (
        <button onClick={() => setIsCollapsed(!isCollapsed)}>
          {isCollapsed ? t("realtor.detailsShow") : t("realtor.detailsHide")}
        </button>
      )}

      {!isCollapsed && (
        <div className="flex flex-col justify-start items-start gap-[25px]">
          {visibleFields.map((field, index) => (
            <ConditionalField
              key={field.key || field.labelKey || index}
              value={field.value}
              labelKey={field.labelKey}
              customLabel={field.customLabel}
              renderValue={field.renderValue}
              forceShow={field.forceShow}
              emptyPlaceholder={field.emptyPlaceholder}
              data-testid={`field-${field.key || field.labelKey || index}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FieldSection;
